import {CookieHelper} from "../modules/CookieHelper";
import {DataLayerHelper} from "../modules/DataLayerHelper";
import {getDataLayer} from "../modules/HelperFunctions";

require('vanilla-cookieconsent');

class CookieConsentHandler
{
    constructor(cc, data_layer_helper, translations) {
        this.cc = cc;
        this.data_layer_helper = data_layer_helper;
        this.translations = translations;

        this.gtm_consents = {
            'ad_storage'        : false,
            'ad_user_data'      : false,
            'ad_personalization': false,
            'analytics_storage' : false,
        };

        this.checkOldCookie();
        this.initListeners();
    }

    setCookieConsent() {
        const self               = this,
              locale             = new Intl.Locale(document.documentElement.lang),
              language           = locale.language,
              translations_local = this.translations[language];

        let languages = [];

        languages[language] =
            {
                consent_modal : {
                    title      : translations_local.consent_modal.title,
                    description: `${translations_local.consent_modal.welcome} ${translations_local.consent_modal.we_use_cookies}
                                        ${translations_local.consent_modal.read_more}
                                        <a target="_blank" href="/algemene-voorwaarden#cookies">
                                            ${translations_local.consent_modal.statement}
                                        </a>.<br>
                                        <button type="button" data-cc="c-settings" class="cc-link">
                                            ${translations_local.consent_modal.manage_settings}
                                        </button>`,
                    primary_btn: {
                        text: translations_local.consent_modal.primary_btn,
                        role: 'accept_all'
                    }
                },
                settings_modal: {
                    title               : translations_local.settings_modal.title,
                    save_settings_btn   : translations_local.settings_modal.save_settings,
                    accept_all_btn      : translations_local.settings_modal.accept_all,
                    close_btn_label     : translations_local.settings_modal.close,
                    cookie_table_headers: [
                        {col1: 'Naam'},
                        {col2: 'Domein'}
                    ],
                    blocks              : [
                        {
                            title      : '',
                            description: `${translations_local.consent_modal.welcome} ${translations_local.consent_modal.we_use_cookies}`
                        },
                        {
                            title      : translations_local.required,
                            description: '',
                            toggle     : {
                                value   : 'necessary',
                                enabled : true,
                                readonly: true
                            }
                        },
                        {
                            title      : translations_local.analytical,
                            description: '',
                            toggle     : {
                                value   : 'analytics',
                                enabled : false,
                                readonly: false
                            }
                        },
                        {
                            title      : translations_local.advertising,
                            description: '',
                            toggle     : {
                                value   : 'advertising',
                                enabled : false,
                                readonly: false
                            }
                        },
                        {
                            title      : translations_local.more_information,
                            description: `${translations_local.consent_modal.read_more}
                                              <a target="_blank" href="/algemene-voorwaarden#cookies">
                                                  ${translations_local.consent_modal.statement}
                                              </a>.`
                        }
                    ]
                }
            };

        this.cc.run({
            current_lang     : language,
            autoclear_cookies: true,
            theme_css        : '/css/app-packages.css',
            page_scripts     : true,
            languages        : languages,
            force_consent    : true,
            gui_options      : {
                consent_modal : {
                    layout    : 'box',               // box/cloud/bar
                    position  : 'bottom right',     // bottom/middle/top + left/right/center
                    transition: 'zoom',           // zoom/slide
                },
                settings_modal: {
                    layout: 'box',                 // box/bar
                    // position: 'left',           // left/right
                    transition: 'zoom'            // zoom/slide
                }
            },
            onAccept         : () => {
                self.updateGtmConsentStatus('analytics_storage', cc.allowedCategory('analytics'));
                self.updateGtmConsentStatus('ad_storage', cc.allowedCategory('advertising'));
                self.updateGtmConsentStatus('ad_user_data', cc.allowedCategory('advertising'));
                self.updateGtmConsentStatus('ad_personalization', cc.allowedCategory('advertising'));

                this.refreshCookies(this.gtm_consents);

                this.updateConsentDatalayer();
            },
            onChange         : (cookie, changed_preferences) => {
                const len = changed_preferences.length;
                for (let i = 0; i < len; i++) {
                    let name = changed_preferences[i];
                    if (cookie.level.includes(name)) {
                        self.updateGtmConsentStatus('analytics_storage', cc.allowedCategory('analytics'));
                        self.updateGtmConsentStatus('ad_storage', cc.allowedCategory('advertising'));
                        self.updateGtmConsentStatus('ad_user_data', cc.allowedCategory('advertising'));
                        self.updateGtmConsentStatus('ad_personalization', cc.allowedCategory('advertising'));
                    } else {
                        self.updateGtmConsentStatus('analytics_storage', 'analytics' === name && cc.allowedCategory('analytics'));
                        self.updateGtmConsentStatus('ad_storage', 'advertising' === name && cc.allowedCategory('advertising'));
                        self.updateGtmConsentStatus('ad_user_data', 'advertising' === name && cc.allowedCategory('advertising'));
                        self.updateGtmConsentStatus('ad_personalization', 'advertising' === name && cc.allowedCategory('advertising'));
                    }
                }

                this.refreshCookies(this.gtm_consents);

                this.updateConsentDatalayer();
            },
        });
    }

    checkOldCookie() {
        const analytics_storage = CookieHelper.getCookie('analytics_storage');
        const ad_storage = CookieHelper.getCookie('ad_storage');
        const ad_user_data = CookieHelper.getCookie('ad_user_data');
        const ad_personalization = CookieHelper.getCookie('ad_personalization');

        const cc_cookie = CookieHelper.getCookie('cc_cookie');

        if ((null === analytics_storage || null === ad_storage || null === ad_user_data || null === ad_personalization) && null !== cc_cookie) {
            this.cc.eraseCookies('cc_cookie');
        }
    }

    refreshCookies(gtm_consents) {
        const expiration_in_days = this.cc.getConfig('cookie_expiration');
        const expiration_date = new Date();
        expiration_date.setDate(expiration_date.getDate() + (expiration_in_days - 1));

        for (const [consent, granted] of Object.entries(gtm_consents)) {
            let granted_status = granted ? 'granted' : 'denied';
            granted_status += `;Expires=${expiration_date.toUTCString()}`;

            CookieHelper.setCookie(consent, granted_status);
        }
    }

    updateConsentDatalayer() {
        this.data_layer_helper.pushEvent({
            'event': 'cookie_banner_consent_update'
        });
    }

    updateGtmConsentStatus(consent_type, granted) {
        this.gtm_consents[consent_type] = granted;
    }

    initListeners() {
        const self = this;

        window.addEventListener('load', function () {
            self.setCookieConsent();
        });
    }
}

const cc = initCookieConsent();
const cookie_consent_handler = new CookieConsentHandler(cc, new DataLayerHelper(getDataLayer(), []), cookie_consent_translations);

